import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Brush, ReferenceArea } from 'recharts';
import DatePicker from 'react-datepicker';
import { format, parseISO } from 'date-fns';
import { Download } from 'lucide-react';
import { purpleAirIdToLocalId, EXCEEDANCE_THRESHOLD } from '../utils/constants';
import { getAirQualityInfo } from '../utils/airQualityUtils';
import { downloadCSV } from '../utils/downloadCSV';
import "react-datepicker/dist/react-datepicker.css";

const COLORS = {
  average: '#8884d8',
  readings: '#82ca9d'
};
const SENSOR_ID = '91811';
const PM25_OUTLIER_THRESHOLD = 500;

// AQI category bands in PM2.5 µg/m³ (EPA breakpoints), drawn as subtle
// background tints so readers can see at a glance whether levels are healthy.
// Colors match getAqiCategory / the Air Quality Legend card.
const AQI_BANDS = [
  { y1: 0, y2: 12, color: '#10B981' },      // Good
  { y1: 12, y2: 35.4, color: '#F59E0B' },   // Moderate
  { y1: 35.4, y2: 55.4, color: '#F97316' }, // Unhealthy for Sensitive Groups
  { y1: 55.4, y2: 150.4, color: '#EF4444' } // Unhealthy
];

// Function to calculate moving average
// eslint-disable-next-line no-unused-vars
const calculateMovingAverage = (data, windowSize = 7) => {
  return data.map((item, index) => {
    const start = Math.max(0, index - Math.floor(windowSize / 2));
    const end = Math.min(data.length, index + Math.floor(windowSize / 2) + 1);
    const window = data.slice(start, end);
    const average = window.reduce((sum, curr) => sum + curr.pm25, 0) / window.length;

    return {
      ...item,
      pm25_smooth: average
    };
  });
};

const formatChartData = (data, type) => {
  if (type === 'county') {
    return data
      .filter(item => item.average <= PM25_OUTLIER_THRESHOLD)
      .map(item => ({
        timestamp: format(parseISO(item.date), 'MMM dd yyyy'),
        rawTimestamp: item.date,
        pm25: item.average,
        date: item.date,
        average: item.average
      }))
      .sort((a, b) => new Date(a.rawTimestamp) - new Date(b.rawTimestamp));
  }

  // For sensor data, create separate points for averages and readings
  const combinedData = [];

  data.forEach(dayData => {
    // Add daily average point
    if (dayData.average <= PM25_OUTLIER_THRESHOLD) {
      combinedData.push({
        timestamp: format(parseISO(dayData.timestamp), 'MMM dd yyyy'),
        rawTimestamp: dayData.timestamp,
        dailyAverage: dayData.average,
        readings: null
      });
    }

    // Add individual readings as separate points
    if (dayData.readings && Array.isArray(dayData.readings)) {
      dayData.readings.forEach(reading => {
        if (reading.pm25 <= PM25_OUTLIER_THRESHOLD) {
          combinedData.push({
            timestamp: format(parseISO(reading.timestamp), 'MMM dd yyyy HH:mm'),
            rawTimestamp: reading.timestamp,
            dailyAverage: null,
            readings: reading.pm25
          });
        }
      });
    }
  });

  return combinedData.sort((a, b) => new Date(a.rawTimestamp) - new Date(b.rawTimestamp));
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border rounded shadow-lg text-xs">
        <p className="font-medium mb-1">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-1" style={{ color: entry.color }}>
            <span className="font-medium">{entry.name}:</span>
            <span>{entry.value?.toFixed(1)} µg/m³</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const HistoricalChart = ({ type = 'county', bare = false }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState(new Date());

  const fetchHistoricalData = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd')
      });

      if (type === 'county') {
        params.append('type', 'county');
      } else {
        params.append('sensorId', SENSOR_ID);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/sensors/historical?${params}`
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();

      const formattedData = formatChartData(result, type);
      setData(formattedData);
      setError(null);
    } catch (error) {
      console.error('Error fetching historical data:', error);
      // Fail silently - keep showing existing data if available
      if (data.length === 0) {
        setError(`Failed to load historical data: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, type]);

  useEffect(() => {
    fetchHistoricalData();
  }, [fetchHistoricalData]);

  const getSensorInfo = (sensorId) => {
    const info = purpleAirIdToLocalId[sensorId];
    return info ? `${info.name} - ${info.position}` : 'Unknown Sensor';
  };

  const handleDownload = () => {
    downloadCSV(data, 'County', startDate, endDate, [], 'historical');
  };

  // Key insights for the selected period (county view): average, worst day,
  // exceedance-day count, and the overall AQI category of the period average.
  const insights = useMemo(() => {
    if (type !== 'county' || data.length === 0) return null;
    const avg = data.reduce((s, d) => s + d.pm25, 0) / data.length;
    const worst = data.reduce((m, d) => (d.pm25 > m.pm25 ? d : m), data[0]);
    const exceedanceDays = data.filter(d => d.pm25 > EXCEEDANCE_THRESHOLD).length;
    return { avg, worst, exceedanceDays, ...getAirQualityInfo(avg) };
  }, [type, data]);

  const chartTitle = type === 'county' ? 'County-Wide Trends' : 'Historical Data';
  const sensorInfo = type === 'county' ? '' : `Sensor: ${SENSOR_ID} - ${getSensorInfo(SENSOR_ID)}`;

  return (
    <div className={bare ? '' : 'p-6 bg-white rounded-lg shadow-md'}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-semibold text-lg">{chartTitle}</h2>
        <div className="flex items-center gap-4">
          <div className="flex gap-2 items-center">
            <button
              onClick={handleDownload}
              className="flex items-center gap-1 px-2 py-1 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors"
              title="Download data as CSV"
            >
              <Download size={16} />
              <span>CSV</span>
            </button>
            <DatePicker
              selected={startDate}
              onChange={date => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              maxDate={endDate}
              className="px-2 py-1 border rounded w-[6.5rem]"
            />
            <span>to</span>
            <DatePicker
              selected={endDate}
              onChange={date => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              maxDate={new Date()}
              className="px-2 py-1 border rounded w-[6.5rem]"
            />
          </div>
        </div>
      </div>

      <div className="mb-2">
        {sensorInfo && <p className="text-sm text-gray-600">{sensorInfo}</p>}
        <p className="text-xs text-gray-600">
          {type === 'county' ?
            `Track how county-wide PM2.5 changes over time to identify periods of poorer air quality. Showing daily averages for ${data.length} days; background tints mark AQI categories (see legend below).` :
            `Showing individual readings and daily averages (excluding readings above ${PM25_OUTLIER_THRESHOLD} µg/m³)`
          }
        </p>
      </div>

      {/* Key insights strip — one compact line summarizing the selected period. */}
      {insights && !loading && !error && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs bg-gray-50 rounded px-2.5 py-1.5 mb-2">
          <span className="text-gray-600">
            Period avg: <span className="font-semibold text-gray-800">{insights.avg.toFixed(1)} µg/m³</span>
          </span>
          <span className="text-gray-600">
            Highest day: <span className="font-semibold text-gray-800">{insights.worst.pm25.toFixed(1)} µg/m³</span>
            <span className="text-gray-400"> ({insights.worst.timestamp})</span>
          </span>
          <span className="text-gray-600">
            Exceedance days: <span className="font-semibold text-gray-800">{insights.exceedanceDays}</span>
          </span>
          <span className="text-gray-600">
            Overall: <span className={`font-semibold ${insights.textColor}`}>{insights.category}</span>
          </span>
        </div>
      )}

      <div className={bare ? 'h-[260px]' : 'h-[300px]'}>
        {loading ? (
          <div className="h-full flex justify-center items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
          </div>
        ) : (
          <div className="bg-gray-50 p-4 rounded-lg h-full">
            <ResponsiveContainer width="99%" height="100%">
              <LineChart
                data={data}
                margin={{ top: 5, right: 30, bottom: 5, left: 10 }}
              >
                {/* Subtle AQI category tints behind the data; bands outside the
                    current y-domain are clipped rather than stretching it. */}
                {type === 'county' && AQI_BANDS.map(b => (
                  <ReferenceArea
                    key={b.y1}
                    y1={b.y1}
                    y2={b.y2}
                    fill={b.color}
                    fillOpacity={0.07}
                    stroke="none"
                    ifOverflow="hidden"
                  />
                ))}
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  tick={{ fontSize: 11 }}
                  interval="preserveStartEnd"
                  minTickGap={50}
                />
                <YAxis
                  label={{
                    value: 'PM2.5 (µg/m³)',
                    angle: -90,
                    position: 'insideLeft',
                    offset: 0,
                    fontSize: 11
                  }}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: '11px' }} />

                {type === 'county' ? (
                  <Line
                    type="monotone"
                    dataKey="pm25"
                    name="County Average PM2.5"
                    stroke={COLORS.average}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                  />
                ) : (
                  <>
                    <Line
                      type="monotone"
                      dataKey="dailyAverage"
                      name="Daily Average PM2.5"
                      stroke={COLORS.average}
                      strokeWidth={2}
                      dot={true}
                      connectNulls
                      isAnimationActive={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="readings"
                      name="Hourly Readings"
                      stroke={COLORS.readings}
                      strokeWidth={1}
                      dot={true}
                      connectNulls
                      isAnimationActive={false}
                    />
                  </>
                )}

                <Brush
                  dataKey="timestamp"
                  height={20}
                  stroke="#8884d8"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoricalChart;